/** `crmEmailDrafts` namespace — en (typed against the French source). */

import type { CrmEmailDraftsKeys } from './fr';

const crmEmailDrafts: CrmEmailDraftsKeys = {
  title: 'Email drafts',
  creditFeatureLabel: 'CRM email drafting',
  compose: 'Write',
  composeTitle: 'Write an email',
  composeTitleFor: 'Write an email — {{name}}',
  editTitle: 'Email draft',
  close: 'Close',
  purposes: {
    fundraising: 'Fundraising',
    investor_update: 'Investor update',
    financial_support: 'Financial support',
    technical_support: 'Technical support',
    content_creation: 'Content creation',
    ambassador: 'Ambassador',
    press_media: 'Press & media',
    partnership: 'Partnership',
  },
  tones: {
    professional: 'Professional',
    warm: 'Warm',
    direct: 'Direct',
    formal: 'Formal',
    friendly: 'Friendly',
    persuasive: 'Persuasive',
  },
  lengths: {
    short: 'Short',
    medium: 'Medium',
    long: 'Long',
  },
  languages: {
    fr: 'French',
    en: 'English',
    ar: 'Arabic',
  },
  statuses: {
    draft: 'Draft',
    sent: 'Sent',
    archived: 'Archived',
  },
  dialog: {
    recipient: 'Recipient',
    recipientLine: 'To: {{name}} <{{email}}>',
    recipientNoEmailLine: 'To: {{name}}',
    noEmailWarning:
      'This contact has no email address. You can still write the draft, but an address must be added before sending.',
    companyLine: 'Organisation: {{name}}',
    recipientContact: 'Recipient contact (optional)',
    recipientContactNone: 'The organisation (no specific contact)',
    recipientContactsLoading: 'Loading contacts…',
    recipientContactsError: 'Could not load the organisation’s contacts.',
    recipientContactNoEmail: '(no email)',
    purpose: 'Purpose',
    purposesLoading: 'Loading purposes…',
    purposeCustomOption: 'Other purpose (custom)…',
    purposeCustomLabel: 'Custom purpose label',
    purposeCustomPlaceholder: 'E.g. invitation to our Montréal event',
    template: 'Template',
    templateNone: 'None - let the AI write',
    templatesLoading: 'Loading templates…',
    templatesError: 'Could not load the templates — you can still write without a template.',
    templateBuiltin: '{{name}} (built-in)',
    tone: 'Tone',
    language: 'Language',
    length: 'Length',
    instructions: 'Instructions (optional)',
    instructionsPlaceholder: 'E.g. mention our meeting at the Montréal trade show',
    productBrief: 'Product brief (optional)',
    productBriefPlaceholder: 'Leave empty to use the workspace brief',
    productBriefHint: 'When this field is empty, the brief saved for your workspace is used.',
    mode: 'Writing mode',
    modeAi: 'AI-written draft',
    modeTemplate: 'Use the template without AI',
    modeTemplateHint: 'Free: the chosen template is merged with the contact’s data, with no AI and no credits.',
    templateRequired: 'Choose a template to use the no-AI mode.',
    generate: 'Generate draft',
    generating: 'Writing…',
    applyTemplate: 'Apply template',
    applying: 'Applying…',
    regenerate: 'Regenerate',
    backToSettings: 'Change settings',
    subject: 'Subject',
    body: 'Message',
    variants: 'Variants',
    variantPrimary: 'Main version',
    variantN: 'Variant {{n}}',
    variantHint: 'Click a variant to put it in the editor.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Draft saved.',
    unsaved: 'Unsaved changes',
    openComposer: 'Open in the email editor',
    addToSequence: 'Add to a sequence',
    persistedNote: 'The draft is saved automatically. Nothing is sent without your action in the email editor.',
    creditsCharged: '{{n}} credit(s) used',
    readOnlyNote: 'Your role lets you view drafts, not edit them.',
    sentNote: 'This draft has already been sent; it can no longer be edited.',
    errors: {
      aiUnavailable: 'The AI assistant is temporarily unavailable. No credits were used. Try again in a moment, or use a template without AI.',
      generateFailed: 'Writing the draft failed. Please try again.',
      saveFailed: 'Could not save the draft. Please try again.',
      retry: 'Try again',
      purposeRequired: 'Choose or type a purpose.',
    },
  },
  sequence: {
    pick: 'Sequence',
    loading: 'Loading sequences…',
    none: 'No active sequence.',
    loadFailed: 'Could not load the sequences.',
    confirm: 'Add',
    adding: 'Adding…',
    result: '{{enrolled}} contact(s) enrolled, {{skipped}} skipped by the CASL / Law 25 check.',
    failed: 'Adding to the sequence failed.',
  },
  list: {
    loading: 'Loading drafts…',
    loadFailed: 'Could not load the email drafts.',
    retry: 'Try again',
    empty: 'No email drafts yet.',
    emptyHint: 'Use “Write” to prepare a purpose-based email.',
    count: '{{shown}} of {{total}}',
    noSubject: '(no subject)',
    approved: 'Approved',
    credits: '{{n}} credit(s)',
    edit: 'Edit',
    view: 'View',
    delete: 'Delete',
    archive: 'Archive',
    unarchive: 'Unarchive',
    openComposer: 'Open in the editor',
    addToSequence: 'Add to a sequence',
    deleteTitle: 'Delete the draft',
    deleteMessage: 'Permanently delete the draft “{{subject}}”?',
    actionFailed: 'The action failed. Please try again.',
    showArchived: 'Show archived',
  },
};

export default crmEmailDrafts;
